import { type QueryClient, useQueryClient } from '@tanstack/react-query';

import { allPreferencesQuery } from '../../../shared/user-preferences/api/all-preferences-query';
import { useCanSavePreferences } from '../../../shared/user-preferences/hooks/useCanSavePreferences';
import {
  PAGE_ORDER_SCOPES,
  type PreferenceScope,
  UserPreferenceKey,
  type UserPreferences,
} from '../../../shared/user-preferences/types';
import {
  enqueuePageOrderSave,
  writePageOrderToCache,
} from '../../../shared/user-preferences/utils/page-order-queue';
import { useEffectiveUserId } from '../../adminRewrite/impersonation/useEffectiveUserId';
import {
  createPage as apiCreatePage,
  deletePage as apiDeletePage,
  updatePage as apiUpdatePage,
} from '../api/build-a-dash-api';
import type { Page } from '../components/PageConfigProvider/types';
import { useOptimisticListMutations } from './use-optimistic-list-mutations';
import { pageQueryKeys } from './usePagesQuery';

type AllPrefs = Record<PreferenceScope, Partial<UserPreferences>>;

/**
 * Returns cached prefs, or fetches them. Ensures cleanup runs even when the
 * delete happens before the preferences modal / tab-order hook have warmed
 * the cache in this session.
 */
async function loadAllPrefs(
  queryClient: QueryClient
): Promise<AllPrefs | undefined> {
  // Your own preferences: the cleanup never runs while impersonating.
  const ownPreferences = allPreferencesQuery(null);
  const cached = queryClient.getQueryData(ownPreferences.queryKey);
  if (cached) return cached;
  try {
    return await queryClient.fetchQuery(ownPreferences);
  } catch (err) {
    console.error('Failed to load preferences for tab-order cleanup:', err);
    return undefined;
  }
}

async function cleanupDeletedPageFromOrder(
  queryClient: QueryClient,
  pageId: string
): Promise<void> {
  const allPrefs = await loadAllPrefs(queryClient);
  if (!allPrefs) return;

  // Mirrors `customTabId` in ./useTabOrder — kept inline here to avoid pulling
  // useTabOrder's module-level deps into this hook + its tests.
  const deletedTabId = `custom:${pageId}`;
  const dirtyScopes = PAGE_ORDER_SCOPES.filter((scope) => {
    const order = allPrefs[scope]?.[UserPreferenceKey.PageOrder] ?? [];
    return order.includes(deletedTabId);
  });
  if (dirtyScopes.length === 0) return;

  try {
    // Read each scope's latest cached order (which any in-flight reset / drag
    // save has already written to via writePageOrderToCache), then sync-write
    // the filtered result before enqueueing so subsequent writers see it too.
    await Promise.all(
      dirtyScopes.map((scope) => {
        const latest = queryClient.getQueryData<AllPrefs>(['user-preferences']);
        const order = latest?.[scope]?.[UserPreferenceKey.PageOrder] ?? [];
        const filtered = order.filter((tabId) => tabId !== deletedTabId);
        writePageOrderToCache(queryClient, scope, filtered);
        return enqueuePageOrderSave(scope, filtered);
      })
    );
  } catch (err) {
    // Stale tab IDs are filtered visually by mergeWithStoredOrder, so a failed
    // cleanup is non-fatal — surface it for observability and move on.
    console.error('Failed to clean up deleted page from tab order:', err);
  }

  await queryClient.invalidateQueries({ queryKey: ['user-preferences'] });
}

export function usePageMutations() {
  const queryClient = useQueryClient();
  const userId = useEffectiveUserId();
  const canSavePreferences = useCanSavePreferences();

  const { create, update, remove } = useOptimisticListMutations<
    Page,
    Page,
    Page,
    boolean
  >({
    listKey: pageQueryKeys.lists(userId),
    createFn: apiCreatePage,
    updateFn: apiUpdatePage,
    deleteFn: apiDeletePage,
    onDeleted: (id) =>
      canSavePreferences
        ? cleanupDeletedPageFromOrder(queryClient, id)
        : undefined,
  });

  /** Drops a page another endpoint deleted (a report document's same-id legacy page). */
  const forgetDeletedPage = (id: string) => {
    queryClient.setQueryData<Page[]>(pageQueryKeys.lists(userId), (pages) =>
      pages?.filter((p) => p.id !== id)
    );
    return canSavePreferences
      ? cleanupDeletedPageFromOrder(queryClient, id)
      : undefined;
  };

  return {
    handleCreatePage: create,
    handleUpdatePage: update,
    handleDeletePage: remove,
    forgetDeletedPage,
  };
}
